import type { AppData, Draw, DrawInvoice, InvoiceStatus, Job, SowItem } from "@/lib/types";

export const roundMoney = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

export function normalizeGcFeePercent(value: unknown) {
  const percent = Number(value ?? 0);
  if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
    throw new Error("GC fee must be between 0% and 100%.");
  }
  return Math.round(percent * 100) / 100;
}

export function gcFeeForSubtotal(subtotal: number, percent: number) {
  const safeSubtotal = Number(subtotal);
  if (!Number.isFinite(safeSubtotal) || safeSubtotal < 0) throw new Error("GC fee subtotal cannot be negative.");
  return roundMoney(safeSubtotal * normalizeGcFeePercent(percent) / 100);
}

export function gcFeePercentForDraws(draws: Draw[]) {
  const percents = new Set(draws.map((draw) => normalizeGcFeePercent(draw.gcFeePercent)));
  if (percents.size > 1) throw new Error("A draw request has inconsistent GC fee snapshots.");
  return percents.size ? [...percents][0] : 0;
}

export function drawWithheldAmount(draw: Draw) {
  if (!["APPROVED", "RECEIVED"].includes(draw.status)) return 0;
  if (Number.isFinite(draw.withheldAmount)) return roundMoney(Number(draw.withheldAmount));
  const percent = Number(draw.holdbackPercent || 0);
  return percent > 0 ? roundMoney(draw.amount * percent / 100) : 0;
}

export function drawApprovedAmount(draw: Draw) {
  if (!["APPROVED", "RECEIVED"].includes(draw.status)) return 0;
  if (Number.isFinite(draw.approvedAmount)) return roundMoney(Number(draw.approvedAmount));
  return roundMoney(draw.amount - drawWithheldAmount(draw));
}

export function drawCommittedAmount(draw: Draw) {
  if (draw.status === "CLIENT_REVIEW") return roundMoney(draw.amount);
  return drawApprovedAmount(draw);
}

export function sowAvailableToRequest(sow: SowItem, draws: Draw[]) {
  const committed = draws
    .filter((draw) => draw.sowId === sow.id)
    .reduce((sum, draw) => sum + drawCommittedAmount(draw), 0);
  return Math.max(0, roundMoney(sow.contractAmount + (sow.approvedBudgetChanges || 0) - committed));
}

export function holdbackForAmount(amount: number, percent: number) {
  const withheldAmount = roundMoney(amount * percent / 100);
  return { withheldAmount, approvedAmount: roundMoney(amount - withheldAmount) };
}

export function invoiceStatusFromDraws(draws: Draw[]): InvoiceStatus {
  if (draws.some((draw) => draw.status === "CLIENT_REVIEW")) return "PENDING_CLIENT_APPROVAL";
  if (draws.some((draw) => draw.status === "CHANGES_REQUESTED")) return "CHANGES_REQUESTED";
  if (draws.length && draws.every((draw) => draw.status === "RECEIVED")) return "PAID";
  if (draws.some((draw) => draw.status === "RECEIVED")) return "PARTIALLY_PAID";
  if (draws.length && draws.every((draw) => draw.status === "APPROVED")) return "APPROVED";
  return "PENDING_CLIENT_APPROVAL";
}

export function jobBudget(job: Job, sowItems: SowItem[]) {
  const original = Number(job.contractAmount) > 0
    ? Number(job.contractAmount)
    : sowItems.reduce((sum, sow) => sum + sow.contractAmount, 0);
  return roundMoney(original + sowItems.reduce((sum, sow) => sum + (sow.approvedBudgetChanges || 0), 0));
}

export function deriveDrawInvoices(data: AppData): DrawInvoice[] {
  const sowById = new Map(data.sowItems.map((sow) => [sow.id, sow]));
  const jobById = new Map(data.jobs.map((job) => [job.id, job]));
  const grouped = new Map<string, Draw[]>();
  for (const draw of data.draws) {
    const requestId = draw.requestId || draw.id;
    grouped.set(requestId, [...(grouped.get(requestId) || []), draw]);
  }

  const groups = [...grouped.entries()].map(([requestId, draws]) => {
    const first = [...draws].sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id))[0];
    const sow = sowById.get(first.sowId);
    const job = sow ? jobById.get(sow.jobId) : undefined;
    return { requestId, draws, first, job };
  }).filter((group): group is typeof group & { job: Job } => Boolean(group.job));

  const committedByJob = new Map<string, number>();
  return groups.sort((left, right) => left.first.createdAt.localeCompare(right.first.createdAt) || left.requestId.localeCompare(right.requestId)).map(({ requestId, draws, first, job }) => {
    const requestedSubtotal = roundMoney(draws.reduce((sum, draw) => sum + draw.amount, 0));
    const gcFeePercent = gcFeePercentForDraws(draws);
    const gcFeeAmount = gcFeeForSubtotal(requestedSubtotal, gcFeePercent);
    const approvedWorkTotal = roundMoney(draws.reduce((sum, draw) => sum + drawApprovedAmount(draw), 0));
    const withheldWorkTotal = roundMoney(draws.reduce((sum, draw) => sum + drawWithheldAmount(draw), 0));
    const decidedWorkTotal = roundMoney(approvedWorkTotal + withheldWorkTotal);
    const decidedGcFee = gcFeeForSubtotal(decidedWorkTotal, gcFeePercent);
    const withheldGcFee = decidedWorkTotal > 0
      ? roundMoney(decidedGcFee * withheldWorkTotal / decidedWorkTotal)
      : 0;
    const approvedGcFee = roundMoney(decidedGcFee - withheldGcFee);
    const requestedTotal = roundMoney(requestedSubtotal + gcFeeAmount);
    const approvedTotal = roundMoney(approvedWorkTotal + approvedGcFee);
    const withheldTotal = roundMoney(withheldWorkTotal + withheldGcFee);
    const committed = roundMoney(draws.reduce((sum, draw) => sum + drawCommittedAmount(draw), 0));
    const jobSows = data.sowItems.filter((sow) => sow.jobId === job.id);
    const committedToDate = roundMoney((committedByJob.get(job.id) || 0) + committed);
    committedByJob.set(job.id, committedToDate);
    const approvedDecisionDraws = draws.filter((draw) => ["APPROVED", "RECEIVED"].includes(draw.status));
    const holdbackPercents = new Set(approvedDecisionDraws.map((draw) => Number(draw.holdbackPercent || 0)));
    const approvedReasons = new Set(approvedDecisionDraws
      .map((draw) => draw.holdbackReason?.trim() || "")
      .filter(Boolean));
    const changeReasons = new Set(draws
      .filter((draw) => draw.status === "CHANGES_REQUESTED")
      .map((draw) => draw.holdbackReason?.trim() || "")
      .filter(Boolean));
    const uniformHoldbackPercent = holdbackPercents.size === 1 ? [...holdbackPercents][0] : 0;
    const headerReason = uniformHoldbackPercent > 0 && approvedReasons.size === 1
      ? [...approvedReasons][0]
      : approvedDecisionDraws.length === 0 && changeReasons.size === 1
        ? [...changeReasons][0]
        : "";
    return {
      requestId,
      requestNumber: first.requestNumber || `LEGACY-${requestId.slice(0, 8).toUpperCase()}`,
      clientId: job.clientId,
      jobId: job.id,
      requestedSubtotal,
      gcFeePercent,
      gcFeeAmount,
      requestedTotal,
      approvedTotal,
      withheldTotal,
      holdbackPercent: uniformHoldbackPercent,
      holdbackReason: headerReason,
      status: invoiceStatusFromDraws(draws),
      remainingContractBalance: roundMoney(jobBudget(job, jobSows) - committedToDate),
      createdAt: first.createdAt,
    };
  });
}

export function effectiveDrawInvoices(data: AppData) {
  return data.invoices?.length ? data.invoices : deriveDrawInvoices(data);
}

export function invoicePaymentDrawIds(data: Pick<AppData, "draws">, requestId: string) {
  return data.draws
    .filter((draw) => (draw.requestId || draw.id) === requestId && draw.status === "APPROVED")
    .map((draw) => draw.id);
}

export function buildSowApprovalQueue(data: AppData, jobId: string) {
  const jobSows = data.sowItems.filter((sow) => sow.jobId === jobId);
  const sowIds = new Set(jobSows.map((sow) => sow.id));
  const sowOrder = new Map(jobSows.map((sow, index) => [sow.id, index]));
  const jobDraws = data.draws.filter((draw) => sowIds.has(draw.sowId));
  const ownerActiveRequestIds = [...new Set(jobDraws
    .filter((draw) => ["CLIENT_REVIEW", "CHANGES_REQUESTED"].includes(draw.status))
    .map((draw) => draw.requestId || draw.id))];
  const activeRequestIds = new Set(ownerActiveRequestIds);
  const requestedDraws = jobDraws
    .filter((draw) => activeRequestIds.has(draw.requestId || draw.id))
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt)
      || (sowOrder.get(left.sowId) ?? 0) - (sowOrder.get(right.sowId) ?? 0)
      || left.id.localeCompare(right.id));
  const requestedSowIds = [...new Set(requestedDraws.map((draw) => draw.sowId))];
  const requestedSowSet = new Set(requestedSowIds);
  const pendingDraws = requestedDraws.filter((draw) => draw.status === "CLIENT_REVIEW");
  const homeownerRequestIds = new Set(pendingDraws.map((draw) => draw.requestId || draw.id));

  return {
    ownerActiveRequestIds,
    requestedDraws,
    requestedSowIds,
    notRequestedSowIds: jobSows.filter((sow) => !requestedSowSet.has(sow.id)).map((sow) => sow.id),
    pendingDraws,
    homeownerReviewDraws: requestedDraws.filter((draw) => homeownerRequestIds.has(draw.requestId || draw.id)),
  };
}

export function sowRemainingAfterRequest(data: AppData, sowId: string, requestId: string) {
  const sow = data.sowItems.find((item) => item.id === sowId);
  if (!sow) return 0;
  const ordered = data.draws
    .filter((draw) => draw.sowId === sowId)
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id));
  let committed = 0;
  for (const draw of ordered) {
    committed = roundMoney(committed + drawCommittedAmount(draw));
    if ((draw.requestId || draw.id) === requestId) break;
  }
  return roundMoney(sow.contractAmount + (sow.approvedBudgetChanges || 0) - committed);
}

export type HoldbackSource = {
  requestId: string;
  requestNumber: string;
  holdbackPercent: number;
  amount: number;
};

export type HoldbackFlow = {
  priorHoldbackAvailable: number;
  priorHoldbackApplied: number;
  priorHoldbackSources: HoldbackSource[];
  newlyWithheld: number;
  holdbackBalanceAfter: number;
};

type HoldbackLot = HoldbackSource & { remaining: number };

export function buildHoldbackLedger(data: Pick<AppData, "draws">) {
  const flows = new Map<string, HoldbackFlow>();
  const pools = new Map<string, HoldbackLot[]>();
  const ordered = [...data.draws].sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id));

  for (const draw of ordered) {
    const pool = pools.get(draw.sowId) || [];
    const priorHoldbackAvailable = roundMoney(pool.reduce((sum, lot) => sum + lot.remaining, 0));
    let amountToApply = draw.status === "CHANGES_REQUESTED" ? 0 : Math.min(draw.amount, priorHoldbackAvailable);
    const priorHoldbackSources: HoldbackSource[] = [];
    for (const lot of pool) {
      if (amountToApply <= 0) break;
      const amount = roundMoney(Math.min(lot.remaining, amountToApply));
      if (amount <= 0) continue;
      priorHoldbackSources.push({ requestId: lot.requestId, requestNumber: lot.requestNumber, holdbackPercent: lot.holdbackPercent, amount });
      lot.remaining = roundMoney(lot.remaining - amount);
      amountToApply = roundMoney(amountToApply - amount);
    }
    const priorHoldbackApplied = roundMoney(priorHoldbackSources.reduce((sum, source) => sum + source.amount, 0));
    const newlyWithheld = drawWithheldAmount(draw);
    if (newlyWithheld > 0) {
      pool.push({
        requestId: draw.requestId || draw.id,
        requestNumber: draw.requestNumber || `LEGACY-${(draw.requestId || draw.id).slice(0, 8).toUpperCase()}`,
        holdbackPercent: Number(draw.holdbackPercent || 0),
        amount: newlyWithheld,
        remaining: newlyWithheld,
      });
    }
    const activePool = pool.filter((lot) => lot.remaining > 0);
    pools.set(draw.sowId, activePool);
    flows.set(draw.id, {
      priorHoldbackAvailable,
      priorHoldbackApplied,
      priorHoldbackSources,
      newlyWithheld,
      holdbackBalanceAfter: roundMoney(activePool.reduce((sum, lot) => sum + lot.remaining, 0)),
    });
  }
  return flows;
}

export function availableHoldbackBySow(data: Pick<AppData, "draws">) {
  const balances = new Map<string, number>();
  const ledger = buildHoldbackLedger(data);
  for (const draw of [...data.draws].sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id))) {
    balances.set(draw.sowId, ledger.get(draw.id)?.holdbackBalanceAfter || 0);
  }
  return balances;
}
